/**
 * Bridge between the web viewer and the EdgePACS Companion desktop app.
 *
 * The companion registers a Windows URL protocol handler (`edgeprint://`)
 * that this file fires to hand off images. The companion is single-instance:
 * if it's already open, the launch is forwarded via named pipe and the images
 * append to whatever the user was already composing. If it's not open, the
 * OS spawns it. Either way the user sees the images appear in the composer.
 *
 * Compact wire format: `seriesId:imageIndex` per image, comma-separated.
 *   edgeprint://compose?study=<studyId>&images=<sid1>:<idx1>,<sid2>:<idx2>,...
 *
 * The composer's PACS client resolves each ref by hitting our existing
 * `/atlrestapi/v2/dicom?seriesId=X&imageIndex=Y` endpoint.
 */

import { getRenderingEngine } from '@cornerstonejs/core';
import { RENDERING_ENGINE_ID } from '../cornerstone/init';

/**
 * Extract our wire-format ref ("seriesId:imageIndex") from a wadouri imageId
 * URL. Returns null if the imageId doesn't match our EDGEPACS URL shape
 * (e.g. wadors URLs from a DICOMweb backend — deferred until we teach the
 * companion how to talk WADO-RS).
 */
export function parseImageRefFromImageId(imageId: string): string | null {
  const m = /seriesId=(\d+)&imageIndex=(\d+)/.exec(imageId);
  return m ? `${m[1]}:${m[2]}` : null;
}

/**
 * Grab the imageRef of whatever's currently shown in the given viewport.
 * Reads from the live Cornerstone viewport (which knows its stack + current
 * index) rather than any React state, so it's always accurate even mid-scroll.
 */
export function currentImageRefFromViewport(vpIndex: number): string | null {
  try {
    const engine = getRenderingEngine(RENDERING_ENGINE_ID);
    const vp: any = engine?.getViewport(`viewport-${vpIndex}`);
    const imageIds: string[] = vp?.getImageIds?.() || [];
    const idx: number = vp?.getCurrentImageIdIndex?.() ?? 0;
    const id = imageIds[idx];
    return id ? parseImageRefFromImageId(id) : null;
  } catch {
    return null;
  }
}

/** Return imageRefs for every slice of a given series. */
export function refsForSeries(seriesId: number, numImages: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < numImages; i++) out.push(`${seriesId}:${i}`);
  return out;
}

/**
 * Fire the deep link. Returns true if the URL was launched (doesn't
 * guarantee the companion actually received it — Windows just tells us the
 * shell dispatch succeeded). The URL cap on Chrome/Edge is ~10K, so several
 * thousand refs are safe; a full CT (~500 slices) is comfortably under.
 *
 * PACS URL + session token are included so the companion can pull DICOM
 * bytes for each image ref without needing to duplicate any auth config.
 * Token in a URL is imperfect (leaks into shell history) but acceptable
 * for a launch-locally scenario; long-term we'd shift to a POST body over
 * a local HTTP endpoint on the companion.
 */
export function sendToComposer(
  studyId: number,
  imageRefs: string[],
  pacsUrl: string,
  token: string | null,
): boolean {
  return fireEdgePrintUrl('compose', studyId, imageRefs, pacsUrl, token);
}

/**
 * Fire the companion's Burn workflow. Does NOT need image refs — the companion
 * uses the study ids + token to fetch all instances itself. Accepts one OR
 * many study ids so a single button hits every selected row in one launch.
 * Same fingerprint caveat as sendToComposer: passes navigator.userAgent so
 * the backend's SHA256(UA + IP-prefix) check matches on the companion's
 * outbound HTTP.
 */
export function sendToBurn(
  studyIds: number | number[],
  pacsUrl: string,
  token: string | null,
): boolean {
  const ids = Array.isArray(studyIds) ? studyIds : [studyIds];
  if (ids.length === 0) return false;
  return fireEdgePrintUrl('burn', ids[0], [], pacsUrl, token, ids);
}

function fireEdgePrintUrl(
  action: 'compose' | 'burn',
  studyId: number,
  imageRefs: string[],
  pacsUrl: string,
  token: string | null,
  studyIds?: number[],
): boolean {
  // Empty basket is a valid case for compose (user just wants to open the
  // composer). Burn never needs image refs — companion fetches from PACS.
  const parts = [
    `study=${encodeURIComponent(String(studyId))}`,
    `pacs=${encodeURIComponent(pacsUrl)}`,
    // The backend's ValidateToken checks a fingerprint = SHA256(UA + IP-prefix)
    // against the stored fingerprint on the token row. If the companion's HTTP
    // client uses a different UA (which it does — .NET HttpClient's default),
    // fingerprint won't match and every DICOM fetch 401s. Pass the browser's
    // UA so the companion can set it on its outbound requests.
    `ua=${encodeURIComponent(navigator.userAgent)}`,
  ];
  if (studyIds && studyIds.length > 1) {
    parts.push(`studies=${encodeURIComponent(studyIds.join(','))}`);
  }
  if (imageRefs.length > 0) {
    parts.splice(1, 0, `images=${encodeURIComponent(imageRefs.join(','))}`);
  } else if (action === 'compose') {
    // Keep the empty images= param on compose for symmetry with older builds
    // that expect it. The burn parser ignores images= entirely.
    parts.splice(1, 0, `images=`);
  }
  if (token) parts.push(`token=${encodeURIComponent(token)}`);
  const url = `edgeprint://${action}?${parts.join('&')}`;
  try {
    // Setting location.href triggers the protocol handler. window.open
    // works too but leaves a blank about:blank tab in some browsers.
    window.location.href = url;
    return true;
  } catch {
    return false;
  }
}

/**
 * Fire an edgeprint:// deep link AND detect whether the Companion is
 * actually installed on this machine, without asking the user.
 *
 * Browsers give us no direct API for "is this protocol registered?", but we
 * can infer it: when the OS successfully hands the URL off to a registered
 * handler, the launched application takes focus and the browser window
 * fires a `blur` event within a few hundred ms. If no blur fires within
 * ~2.5s, we assume the protocol isn't registered.
 *
 * The user-supplied `fire()` should synchronously trigger the deep link
 * (typically by setting `window.location.href = 'edgeprint://...'`).
 *
 * Trade-offs:
 *   - Chrome's built-in "Open this address in EdgePACS Companion?" prompt
 *     doesn't itself steal focus, so if the user pauses on it for >2.5s and
 *     then clicks Open, we'll incorrectly show the install dialog. First
 *     use may see this; once the user checks "Always allow", subsequent
 *     launches skip the prompt and blur fires immediately.
 *   - If the user explicitly cancels Chrome's prompt, we'll show the
 *     install dialog. This is a false positive but not catastrophic; the
 *     user can just close it.
 * Both edge cases are acceptable trade-offs to avoid interrupting the 99%
 * case with a "close me" dialog every time.
 */
export function launchCompanion(fire: () => boolean): Promise<'launched' | 'not_installed'> {
  return new Promise((resolve) => {
    let blurred = false;
    const onBlur = () => { blurred = true; };
    window.addEventListener('blur', onBlur);

    const ok = fire();
    if (!ok) {
      window.removeEventListener('blur', onBlur);
      resolve('not_installed');
      return;
    }

    setTimeout(() => {
      window.removeEventListener('blur', onBlur);
      resolve(blurred ? 'launched' : 'not_installed');
    }, 2500);
  });
}

/**
 * URL to the EdgePACS Companion installer, offered as a fallback when the
 * deep link doesn't launch anything (usually because the Companion isn't
 * installed). Defaults to <PACS>/downloads/EdgePACS-Companion-Setup.exe -
 * that's the friendly name we rename to at the end of publish.ps1 (vpk's
 * raw output is EdgePrintCompanion-win-Setup.exe; we rename to keep the
 * Downloads folder recognisable to users, without changing --packId which
 * would break the Velopack delta-update stream for existing installs).
 *
 * Override in localStorage under `edgeprint-installer-url` to point elsewhere
 * (e.g. a CDN or intranet share) without rebuilding.
 */
export function getEdgePrintInstallerUrl(pacsUrl: string): string {
  const override = localStorage.getItem('edgeprint-installer-url');
  if (override) return override;
  return `${pacsUrl.replace(/\/$/, '')}/downloads/EdgePACS-Companion-Setup.exe`;
}
